/* eslint-disable react-refresh/only-export-components -- провайдер и его хук
   живут вместе: контекст между ними не должен утекать наружу. */
import type { ReactNode } from 'react'
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react'
import { isApiError, type AuthTokens, type TokenStore } from '@dorixona/api'
import {
  can as canDo,
  canSeeReviews,
  permissionModule,
  type PermissionLetter,
} from '../rbac/permissions'
import { env } from '../../config/env'
import { unregisterPush } from '../notifications/push'
import type { AdminAuthApi, AdminMe } from './api'

export interface Session {
  /** Тот же API входа, которым живёт сессия: экраны входа берут его отсюда. */
  authApi: AdminAuthApi
  /** Кто вошёл; null — вход не выполнен или профиль ещё грузится. */
  me: AdminMe | null
  /** Профиль запрошен, но ещё не пришёл: каркас ждёт, а не мигает входом. */
  loading: boolean
  /** Имя вошедшего для журналов и подписей; без входа — пустая строка. */
  actorName: string
  /** Супер-админ видит сеть целиком и не ограничен потоком статусов. */
  isSuper: boolean
  /** Аптека сотрудника; 0 — вся сеть (В-38). */
  storeId: number
  can: (module: string, letter?: PermissionLetter) => boolean
  /**
   * Временный пароль виден уже в ответе на вход: профиль до смены не отдадут.
   * С ним передаются логин и пароль, которыми вошли, — экран смены пароля не
   * просит набрать временный ещё раз.
   */
  signIn: (tokens: AuthTokens, mustChangePassword?: boolean, credentials?: Credentials) => void
  signOut: () => void
  /**
   * Текущий токен доступа; null — входа нет. Нужен живым событиям: браузер не
   * ставит заголовки на WebSocket, и токен уходит в адресе.
   */
  accessToken: () => string | null
  /** Пароль временный: до смены сервер не пустит никуда, кроме смены пароля. */
  mustChangePassword: boolean
  /**
   * Чем вошли с временным паролем. null — неизвестно: страницу перезагрузили,
   * и временный пароль придётся ввести заново. Живёт только в памяти.
   */
  credentials: Credentials | null
  passwordChanged: (tokens: AuthTokens) => void
}

export interface Credentials {
  login: string
  password: string
}

const SessionContext = createContext<Session | null>(null)

export interface SessionProviderProps {
  children: ReactNode
  api: AdminAuthApi
  tokenStore: TokenStore
  /** Готовый профиль: так тесты экранов открываются без входа и без сети. */
  initialMe?: AdminMe
}

export function SessionProvider({ children, api, tokenStore, initialMe }: SessionProviderProps) {
  const [authenticated, setAuthenticated] = useState(() => tokenStore.get() !== null)
  const [mustChange, setMustChange] = useState(false)
  const [credentials, setCredentials] = useState<Credentials | null>(null)

  // Подставленный профиль живёт до выхода: «выйти» должно возвращать к форме
  // входа и в тесте, иначе экран остаётся запертым.
  const [seeded, setSeeded] = useState<AdminMe | undefined>(initialMe)
  const [loaded, setLoaded] = useState<AdminMe | null>(null)
  const [failed, setFailed] = useState(false)

  /**
   * Профиль запрашиваем один раз на вход. Через react-query это потребовало бы
   * провайдера запросов в каждом тесте экрана, а запрос здесь ровно один.
   * С временным паролем не спрашиваем вовсе: сервер ответит 401.
   */
  const needsProfile = authenticated && !mustChange && seeded === undefined
  useEffect(() => {
    if (!needsProfile) return
    let ignore = false
    void api
      .me()
      .then((next) => {
        if (!ignore) setLoaded(next)
      })
      .catch((error: unknown) => {
        if (ignore) return
        // Токен живой, но пароль временный: вошли раньше и перезагрузили
        // страницу или открыли вторую вкладку. Флаг из ответа на вход не
        // пережил перезагрузку — это не повод выкидывать на форму входа.
        if (isApiError(error) && error.code === 'password_change_required') {
          setMustChange(true)
          return
        }
        setFailed(true)
      })
    return () => {
      ignore = true
    }
  }, [api, needsProfile])

  const me = seeded ?? loaded

  // Профиль не отдали — токен мёртв. Чистим его и показываем вход: иначе
  // каркас навсегда остался бы в состоянии «загружаем профиль».
  useEffect(() => {
    if (!failed) return
    tokenStore.set(null)
    setAuthenticated(false)
    setFailed(false)
  }, [failed, tokenStore])

  const signIn = useCallback(
    (tokens: AuthTokens, mustChangePassword = false, used: Credentials | null = null) => {
      tokenStore.set(tokens)
      setMustChange(mustChangePassword)
      setCredentials(mustChangePassword ? used : null)
      setAuthenticated(true)
    },
    [tokenStore],
  )

  const signOut = useCallback(() => {
    const tokens = tokenStore.get()
    if (tokens) {
      const logout = () => api.logout(tokens.refreshToken).catch(() => undefined)
      // Сначала отвязываем браузер от push — токеном уходящей сессии, пока
      // сервер его принимает; иначе следующий за этим компьютером получал бы
      // чужие уведомления. Ждём не дольше 3 с, сбой выход не держит.
      if (env.features.notifications) void unregisterPush(api, tokens.accessToken).then(logout)
      else void logout()
    }
    tokenStore.set(null)
    setAuthenticated(false)
    setMustChange(false)
    setCredentials(null)
    setLoaded(null)
    setSeeded(undefined)
  }, [api, tokenStore])

  const accessToken = useCallback(() => tokenStore.get()?.accessToken ?? null, [tokenStore])

  const passwordChanged = useCallback(
    (tokens: AuthTokens) => {
      tokenStore.set(tokens)
      setMustChange(false)
      setCredentials(null)
      setAuthenticated(true)
    },
    [tokenStore],
  )

  const value = useMemo<Session>(
    () => ({
      authApi: api,
      me,
      loading: needsProfile && me === null && !failed,
      actorName: me?.name ?? '',
      isSuper: me?.isSystem ?? false,
      storeId: me?.storeId ?? 0,
      can: (module, letter = 'r') =>
        // Отзывы — только чтение и только супер-админу, без права в сетке ролей.
        module === 'reviews'
          ? letter === 'r' && canSeeReviews(me?.roleId ?? 0)
          : canDo(
              me?.permissions ?? {},
              permissionModule(module, { stories: env.storiesMock, news: env.newsMock }),
              letter,
            ),
      signIn,
      signOut,
      accessToken,
      mustChangePassword: mustChange,
      credentials,
      passwordChanged,
    }),
    [
      api,
      me,
      needsProfile,
      failed,
      mustChange,
      credentials,
      signIn,
      signOut,
      accessToken,
      passwordChanged,
    ],
  )

  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>
}

export function useSession(): Session {
  const session = useContext(SessionContext)
  if (!session) throw new Error('useSession вызван вне SessionProvider')
  return session
}
